import React, {useContext, useRef} from 'react';
import {useOktaAuth} from '@okta/okta-react';
import ErrorAlertComponent from '../components/ErrorAlert.component';
import {UserContext} from '../context/userContext';
import {useLocation, useNavigate} from 'react-router-dom';
import AuthService from '../services/auth.service';

interface LoginCallbackProps {
  errorComponent?: React.ComponentType<{ error: Error }>;
  loadingElement?: React.ReactElement;
}

const LoginCallback: React.FC<LoginCallbackProps> = ({ loadingElement = null }) => {

    const { oktaAuth, authState } = useOktaAuth();
    const [callbackError, setCallbackError] = React.useState<Error | null>(null);
    const { signinUser } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const processingStarted = useRef(false);


    React.useEffect(() => {

        const processOktaCallback = async (): Promise<void> => {
            // Use ref to prevent multiple executions in the same component instance
            // This handles React StrictMode double mounting

            if (processingStarted.current) {
                return;
            }
            processingStarted.current = true;

            // Extract the OAuth parameters from URL
            const urlParams = new URLSearchParams(location.search);
            const code = urlParams.get('code');
            const state = urlParams.get('state');


            if (!code || !state) {
                setCallbackError(new Error('Invalid OAuth callback'));
                return;
            }

            try {

                // Handle the Okta redirect - this consumes the authorization code
                await oktaAuth.handleRedirect();


                // Get all tokens from Okta

                const accessToken = await oktaAuth.tokenManager.get('accessToken');
                if (!accessToken) {
                    throw new Error('Unable to retrieve access token from Okta');
                }

                const refreshToken = await oktaAuth.tokenManager.get('refreshToken');

                const idToken = await oktaAuth.tokenManager.get('idToken');

                // Exchange Okta tokens for backend JWT

                const response = await AuthService.signinWithTokens({
                    // @ts-ignore
                    accessToken: accessToken.accessToken,
                    // @ts-ignore
                    idToken: idToken?.idToken,
                    // @ts-ignore
                    refreshToken: refreshToken?.refreshToken
                });


                // Save user to localStorage and context
                signinUser(response.data);

                // DO NOT clear Okta tokens here - the Security provider needs them
                // to update its internal authentication state
                // oktaAuth.tokenManager.clear(); // REMOVED - was causing auth state mismatch

                // Check if there's a redirect URL stored (from ProtectedRoute or from Okta)
                let redirectUrl = sessionStorage.getItem('redirectUrl');

                // Also check if Okta stored an original URI
                await oktaAuth.getOriginalUri();

                // Always redirect to workspace after successful authentication
                // This ensures consistent behavior for both Okta and traditional auth
                redirectUrl = '/react/workspace';


                // Clear any stored redirect URLs
                sessionStorage.removeItem('redirectUrl');
                // Clear Okta's stored URI as well
                await oktaAuth.removeOriginalUri();


                // Use window.location for a hard redirect to ensure the page loads properly
                // This avoids issues with React Router not properly navigating after OAuth callback
                window.location.href = `/isam${redirectUrl}`;


            } catch (error: any) {
                setCallbackError(error);
                // Reset the flag on error to allow retry
                processingStarted.current = false;
            }
        };

        processOktaCallback();
    }, [oktaAuth, navigate, signinUser, location.search]);

    const authError = authState?.error;
    const displayError = callbackError || authError;


    if (displayError) {
        return <ErrorAlertComponent message={displayError?.message} />;
    }

    return loadingElement;
};

export default LoginCallback;